/**
 * /logistica/cargas — Lista de Cargas + filtro por data (agenda).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiPlus, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  listarCargas,
  SITUACAO_CARGA_LABELS,
  SITUACAO_CARGA_TONES,
} from "../../../services/logistica";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}


export default function CargasLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [filtroData, setFiltroData] = useState("");

  const podePlanejar = isMaster || hasAllPermissions(["logistica:planejar"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarCargas(request, {
        situacao: filtroSituacao || undefined,
        data: filtroData || undefined,
      });
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(list);
    } catch (err) {
      setError(err?.message || "Erro ao carregar cargas.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request, filtroSituacao, filtroData]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => {
    const planejadas = items.filter((c) => c.situacao === "PLANEJADA").length;
    const emRota = items.filter((c) => c.situacao === "EM_ROTA").length;
    const concluidas = items.filter((c) => c.situacao === "CONCLUIDA").length;
    return [
      { label: "Total na lista", value: items.length, tone: "neutral" },
      { label: "Planejadas", value: planejadas, tone: planejadas > 0 ? "info" : "neutral" },
      { label: "Em rota", value: emRota, tone: emRota > 0 ? "warning" : "neutral" },
      { label: "Concluídas", value: concluidas, tone: "success" },
    ];
  }, [items]);

  return (
    <PageContainer>
      <EntityHeader
        title="Cargas"
        subtitle="Agenda de saídas de caminhão - ADR-0026"
        actions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregar,
          },
          ...(podePlanejar
            ? [{
                label: "Nova carga",
                icon: <FiPlus size={14} aria-hidden="true" />,
                onClick: () => navigate("/logistica/cargas/criar"),
                primary: true,
              }]
            : []),
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Situação</span>
            <Select
              value={filtroSituacao}
              onChange={(e) => setFiltroSituacao(e.target.value)}
              disabled={loading}
            >
              <option value="">Todas</option>
              {Object.entries(SITUACAO_CARGA_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Data planejada</span>
            <Input
              type="date"
              value={filtroData}
              onChange={(e) => setFiltroData(e.target.value)}
              disabled={loading}
            />
          </label>
          <button
            type="button"
            onClick={() => { setFiltroData(hojeISO()); setFiltroSituacao(""); }}
            style={{
              padding: "8px 14px",
              border: "1px solid var(--border)",
              borderRadius: 6,
              background: "var(--surface)",
              cursor: "pointer",
              marginTop: 20,
              color: "inherit",
            }}
          >
            Hoje
          </button>
        </div>
      </Card>

      {error && <ErrorMsg><FiAlertTriangle size={14} /> {error}</ErrorMsg>}

      <Card>
        <SectionTitle>
          {loading ? "Carregando..." : `${items.length} carga(s)`}
        </SectionTitle>
        {!loading && items.length === 0 && (
          <EmptyMsg>Nenhuma carga com os filtros atuais.</EmptyMsg>
        )}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Data</th>
                  <th>Veículo</th>
                  <th>Motorista</th>
                  <th>Peças</th>
                  <th>Situação</th>
                  <th>Saída</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/logistica/cargas/${c.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><strong>{c.numero}</strong></td>
                    <td>{c.data_planejada}</td>
                    <td>{c.veiculo_placa}</td>
                    <td>{c.motorista_nome}</td>
                    <td style={{ textAlign: "center" }}>{c.qtd_oes}</td>
                    <td>
                      <Pill $tone={SITUACAO_CARGA_TONES[c.situacao] || "neutral"}>
                        {SITUACAO_CARGA_LABELS[c.situacao] || c.situacao}
                      </Pill>
                    </td>
                    <td>{c.saiu_em_rota_em ? fmtData(c.saiu_em_rota_em) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
    </PageContainer>
  );
}
